import { browserHistory } from 'react-router';
import { connect } from 'react-redux';
import React, { createRef } from 'react';
/** import third-party libraries in the section above, Sort Lines Ascending */

import TextField from '@mui/material/TextField';
/** import mui components in the section above, Sort Lines Ascending */

import { BlockLoadGroup } from 'widgets-v5/load-group';
import { ButtonGroup, NeutralButton, PrimaryButton } from 'widgets-v5/buttons';
import Card from 'widgets-v5/card';
import CenterLayout from 'widgets-v5/center-layout';
import FormField from 'widgets-v5/form-field';
import Spacer from 'widgets-v5/spacer';
/** import widgets in the section above, Sort Lines Ascending */

import { getApiErrorMessage } from 'utils/http/get-api-error-message';
import { login } from 'states/profile/actions';
import config from '../../../../config';
import createHttp from 'utils/http';
/** import single application imports in the section above, Sort Lines Ascending */

const http = createHttp({ redirectOnFail: false });

// ENVs where /password-login can be opened directly (address bar) without LoginV2 state:
// development, cloud-dev, spain-staging.
const PASSWORD_LOGIN_DIRECT_ACCESS_ENVS = ['development', 'cloud-dev', 'spain-staging'];

/**
 * Password sign-in screen at `/password-login`.
 *
 * Entry paths:
 * - LoginV2 redirect after HTTP 405 (email prefilled, field disabled)
 * - Direct URL on allowed ENVs only (editable email for staging/local testing)
 */
class PasswordLogin extends React.Component {
    constructor(props) {
        super(props);
        this.emailField = createRef();
        this.passwordField = createRef();
    }

    state = {
        message: '',
        noEmail: true,
        noPassword: true,
        showError: false,
        isLoading: false,
        email: '',
    };

    handleSubmit = e => {
        e.preventDefault();

        const email = this.emailField.current.value;
        const password = this.passwordField.current.value;

        if (!email || !password) {
            this.setState({
                showError: true,
                noEmail: !email,
                noPassword: !password,
            });
            return;
        }

        this.setState({
            isLoading: true,
            noEmail: false,
            noPassword: false,
        });

        this.login(email, password).then(
            data => {
                this.props.dispatch(login(data)).then(() => {
                    browserHistory.push('/dashboard');
                });
            },
            err => {
                this.setState({ isLoading: false });
                const code = err?.code;
                if (code >= 400 && code <= 499) {
                    if (err.body === 'Password expired') {
                        this.setState({
                            showError: true,
                            message: 'Your password has expired. Please create a new one.',
                        });
                        return;
                    }
                    if (code === 405) {
                        this.setState({
                            showError: true,
                            message:
                                'Email link login is enabled for this account. Go back and login with email link.',
                        });
                        return;
                    }
                    if (code === 403) {
                        // Urban returns body ["Forbidden"] for bad credentials; keep generic copy.
                        this.setState({
                            showError: true,
                            message: 'Email and password combination incorrect',
                        });
                        return;
                    }
                    this.setState({
                        showError: true,
                        // Other 4xx: prefer API validation message when present.
                        message: getApiErrorMessage(
                            err,
                            'An error has occurred, please try again.'
                        ),
                    });
                } else if ((code >= 500 && code <= 599) || code === void 0) {
                    this.setState({
                        showError: true,
                        message: 'Server error, please try again later',
                    });
                }
            }
        );
    };

    login = (email, password) => {
        return http.post('auth?populate=user,role,organization', { email, password });
    };

    /**
     * Navigate to forgot-password, carrying the typed email so the destination
     * form can be prefilled when available.
     */
    handleResetPassword = () => {
        const email = this.state.email && this.state.email.trim();

        browserHistory.push({
            pathname: '/forgot-password',
            state: email ? { email } : void 0,
        });
    };

    componentDidMount() {
        const email = this.props.location.state?.email;
        if (email) {
            this.setState({ email, noEmail: false });
        } else if (!PASSWORD_LOGIN_DIRECT_ACCESS_ENVS.includes(config.ENV_NAME)) {
            // Production: email-first login only via `/`; password screen requires LoginV2 405 redirect.
            browserHistory.push('/');
        }
    }

    render() {
        const emailFromLoginRedirect = Boolean(this.props.location.state?.email);

        return (
            <CenterLayout className="ef5-login">
                <div>
                    <img className="ef5-login__logo" src="/images/EngageFront-logo.png" />
                    <BlockLoadGroup isLoading={this.state.isLoading} size="large">
                        <form ref="login-form" method="POST" onSubmit={this.handleSubmit}>
                            <Card
                                className="ef5-login__card"
                                title="Sign In"
                                footer={
                                    <div className="ef5-login__card-footer">
                                        <PrimaryButton text="Back" link="/" />
                                        <ButtonGroup>
                                            <NeutralButton
                                                text="Reset Password"
                                                onClick={this.handleResetPassword}
                                            />
                                            <PrimaryButton
                                                efEvent="LOGIN__SIGN_IN"
                                                text="Sign In"
                                                onClick={this.handleSubmit}
                                            />
                                        </ButtonGroup>
                                    </div>
                                }
                            >
                                <FormField
                                    label="E-mail"
                                    isRequired={true}
                                    showErrors={this.state.showError}
                                    errors={this.state.noEmail ? ['E-mail is required'] : null}
                                >
                                    <TextField
                                        inputRef={this.emailField}
                                        fullWidth
                                        variant="outlined"
                                        type="email"
                                        autoComplete="username"
                                        onChange={e => this.setState({ email: e.target.value })}
                                        value={this.state.email}
                                        disabled={emailFromLoginRedirect}
                                    />
                                </FormField>
                                <Spacer />
                                <FormField
                                    label="Password"
                                    isRequired={true}
                                    showErrors={this.state.showError}
                                    errors={this.state.noPassword ? ['Password is required'] : null}
                                >
                                    <TextField
                                        inputRef={this.passwordField}
                                        fullWidth
                                        variant="outlined"
                                        type="password"
                                        autoComplete="current-password"
                                    />
                                </FormField>
                                <FormField
                                    showErrors={this.state.showError}
                                    errors={[this.state.message]}
                                />
                            </Card>
                        </form>
                    </BlockLoadGroup>
                </div>
            </CenterLayout>
        );
    }
}

function select(state) {
    const o = _.get(state, 'profile', {});
    return o;
}

export default connect(select)(PasswordLogin);
